import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Shield, ChevronDown, UserCircle } from "lucide-react";
import logo from "@/assets/logo.png";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

const links = [
  { label: "Capabilities", href: "#capabilities" },
  { label: "AI Core", href: "#ai-core" },
  { label: "Reviews", href: "#reviews-marquee" },
  { label: "Ecosystem", href: "#outro" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const scrollToReviews = (e, href) => {
    if (href === "#reviews-marquee") {
      e.preventDefault();
      document.querySelector("[data-testid='reviews-marquee']")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <a href="#hero" data-testid="brand-logo" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="Autonomous Dex"
              className="w-9 h-9 sm:w-10 sm:h-10 drop-shadow-[0_0_12px_rgba(0,240,255,0.5)] transition-transform group-hover:scale-110"
            />
            <span className="font-heading font-extrabold text-sm sm:text-base tracking-widest text-white">
              AUTONOMOUS<span className="text-gradient-accent"> DEX</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => scrollToReviews(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 hover:text-cyan-400 transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://autonodex.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-autonodex-link"
              className="font-mono text-xs uppercase tracking-[0.18em] text-cyan-400 hover:text-cyan-300 transition-colors duration-300"
            >
              autonodex.org
            </a>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  data-testid="account-menu-button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="btn-ghost-electric flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white"
                >
                  <User className="w-4 h-4 text-cyan-400" />
                  <span className="max-w-[90px] truncate">{user.name || "Account"}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {menuOpen && (
                  <div
                    data-testid="account-menu"
                    className="absolute right-0 mt-2 w-48 rounded-xl bg-black/90 backdrop-blur-xl border border-white/10 p-2 shadow-2xl"
                  >
                    {isAdmin && (
                      <button
                        data-testid="nav-admin-link"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/admin");
                        }}
                        className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-slate-200 hover:bg-white/5 hover:text-cyan-400 transition-colors"
                      >
                        <Shield className="w-4 h-4" /> Admin Panel
                      </button>
                    )}
                    <button
                      data-testid="nav-profile-link"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/profile");
                      }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-slate-200 hover:bg-white/5 hover:text-cyan-400 transition-colors"
                    >
                      <UserCircle className="w-4 h-4" /> My Profile
                    </button>
                    <button
                      data-testid="nav-logout"
                      onClick={() => {
                        setMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-slate-200 hover:bg-white/5 hover:text-red-400 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="nav-login-button"
                onClick={() => setAuthOpen(true)}
                className="btn-electric text-black font-bold text-xs sm:text-sm px-4 sm:px-6 py-2.5 rounded-full"
              >
                Log In
              </button>
            )}
          </div>
        </div>
      </nav>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};
